import React, {Component} from 'react'
import {connect} from "react-redux"
import {withTranslation} from "react-i18next"
import {bindActionCreators} from "redux"
import Moment from "moment/moment"

import {actions as employeeActions} from "../../../actions/employee"
import employeeService from "../../../services/Employee.service"
import {contains, formatCurrency, formatNumber, formatPercentage} from "../../../utils/jsUtils"

import VestsArea from "./VestsArea"
import {
  AWARD_REGION_CODE, AWARD_GRANT_TYPE, AWARD_SUMMARY_OPTIONS,
  DETAILED_TAX_INFORMATION_OPTIONS, VEST_OR_TAX_OPTIONS,
  ROW_HEADER_LEFT_MARGIN,
  ROW_HEADER_TITLE_FONT_WEIGHT, ROW_HEADER_TITLE_FONT_SIZE, ROW_HEADER_INFO_FONT_SIZE
} from "./common"
import {RowActionMenu} from "./RowActionMenu";
import DocsArea from "./DocsArea"
import UnvestedAwardDetailsDialog from "../common/dialogs/UnvestedAwardDetailsDialog"
import VestedAwardDetailsDialog from "../common/dialogs/VestedAwardDetailsDialog"

import {View, Row, Col} from '@amzn/stencil-react-components/layout'
import {ExpanderContent} from '@amzn/stencil-react-components/expander'
import {TriggerButton} from '@amzn/stencil-react-components/helpers'
import {H3, Text} from '@amzn/stencil-react-components/text'
import {Button} from '@amzn/stencil-react-components/button'
import {IconQuestionCircle} from '@amzn/stencil-react-components/icons'
import {Popover} from '@amzn/stencil-react-components/popover'
import {Chip} from '@amzn/stencil-react-components/chip'
import {Link} from '@amzn/stencil-react-components/link'
import {WithBreakpoints} from "@amzn/stencil-react-components/responsive"

class AwardRow extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isExpanded: false,
      isUnvestedAwardDialogVisible: false,
      isVestedAwardDialogVisible: false,
      selectedDoc: null
    };
  }

  onDocumentClick = (doc) => {
    this.setState({
        selectedDoc: doc
      })
    const clearSelectedDoc = () => this.setState({ selectedDoc: null });
    this.props.actions.employee.getDocument(this.props.employee.id, this.props.award.id, doc.id)
      .then(clearSelectedDoc)
      .catch(clearSelectedDoc);
  }

  onRowExpandButtonClick() {
    this.setState({
      isExpanded: !this.state.isExpanded
    });
  }

  onRowMenuItemClick = (key) => {
    this.props.onRowMenuItemClick(key, this.props.award.id);
  }

  onTransactionClick = (transaction) => {
    this.setState(
      {
        isVestedAwardDialogVisible: true,
        award: this.props.award,
        transaction: transaction
      }
    )
  }

  onVestClick = (vest) => {
    this.setState({
        isUnvestedAwardDialogVisible: true,
        vest: vest
      }
    )
  }

  shouldShowTaxRateChangeMenuOption = (award) => {
    if (this.props.fractionalMode && !this.props.isTaxHidden && employeeService.canChangeAwardTaxRate(award)) {
      return true;
    }

    return false;
  }

  getRowMenuOptions(t, award) {
    let options = [];
    options.push({
      value: AWARD_SUMMARY_OPTIONS.VIEW_BY_PDF,
      label: t('StockAwards.Award.RowMenu.ViewPdfOption')
    });
    options.push({
      value: AWARD_SUMMARY_OPTIONS.DOWNLOAD_EXCEL,
      label: t('StockAwards.Award.RowMenu.DownloadExcelOption')
    });
    options.push({
      value: DETAILED_TAX_INFORMATION_OPTIONS.VIEW_BY_PDF,
      label: t('StockAwards.Award.RowMenu.ViewDetailedPdfOption')
    });
    options.push({
      value: DETAILED_TAX_INFORMATION_OPTIONS.DOWNLOAD_EXCEL,
      label: t('StockAwards.Award.RowMenu.DownloadDetailedExcelOption')
    });
    options.push({
      value: DETAILED_TAX_INFORMATION_OPTIONS.VIEW_STATEMENT,
      label: t('StockAwards.Award.RowMenu.ViewStatementOption')
    });

    if (this.props.fractionalMode && !award.fully_vested) {
      options.push({
        value: VEST_OR_TAX_OPTIONS.VEST_ELECTION,
        label: t('StockAwards.Award.RowMenu.VestElectionOption')
      });
    }

    if (this.shouldShowTaxRateChangeMenuOption(award)) {
      options.push({
        value: VEST_OR_TAX_OPTIONS.US_FED_TAX_RATE,
        label: t('StockAwards.Award.RowMenu.USFedRateOption')
      });
    }

    return options;
  }

  getElectionText(t, vest) {
    if (vest) {
      if (this.props.fractionalMode) {
        return employeeService.getVestFractionalElectionText(t, this.props.employee, vest);
      } else {
        return t(`TaxTreatment.${vest.tax_election.code}`);
      }
    } else {
      return "";
    }
  }

  getTaxBadge(award, locale) {
    const taxRates = employeeService.getVestTaxRates(award);

    if (!taxRates) {
      return null;
    }

    const taxMinRateString = formatPercentage(locale, taxRates.min);
    const taxMaxRateString = formatPercentage(locale, taxRates.max);

    if (taxRates.max === 0 || taxRates.max === taxRates.min) {
      if (taxRates.max !== this.props.defaultTaxRate) {
        return taxMinRateString;
      }
    } else {
      return `${taxMinRateString} - ${taxMaxRateString}`;
    }
  }

  getAwardGrantBadge(t, grantType, regionCode) {
    let badgeLabel = null;
    let badgeText = null;

    if (grantType === AWARD_GRANT_TYPE.ISRRSU) {
      badgeLabel = t('StockAwards.Award.IsraeliQualifiedBadgeLabel');
      badgeText = t('StockAwards.Award.IsraeliQualifiedTooltipText');
    }

    if (regionCode === AWARD_REGION_CODE.FRA) {
      if (contains([AWARD_GRANT_TYPE.FRARSU, AWARD_GRANT_TYPE.FRARSUNB], grantType)) {
        badgeLabel = t('StockAwards.Award.FrenchQualifiedPreMacronBadgeLabel');
        badgeText = t('StockAwards.Award.FrenchQualifiedTooltipText');
      }
      if (contains([AWARD_GRANT_TYPE.FRARSU2NB, AWARD_GRANT_TYPE.FRARSU2], grantType)) {
        badgeLabel = t('StockAwards.Award.FrenchQualifiedMacron2BadgeLabel');
        badgeText = t('StockAwards.Award.FrenchQualifiedTooltipText');
      }
      if (contains([AWARD_GRANT_TYPE.FRARSU3], grantType)) {
        badgeLabel = t('StockAwards.Award.FrenchQualifiedMacron3BadgeLabel');
        badgeText = t('StockAwards.Award.FrenchQualifiedTooltipText');
      }
      if (contains(
        [AWARD_GRANT_TYPE.EXCHNG_OLD, AWARD_GRANT_TYPE.RSU, AWARD_GRANT_TYPE.NQ, AWARD_GRANT_TYPE.EXCHNG_NEW, AWARD_GRANT_TYPE.FRANQ], grantType
      )) {
        badgeLabel = t('StockAwards.Award.FrenchNonQualifiedBadgeLabel');
        badgeText = t('StockAwards.Award.FrenchQualifiedTooltipText');
      }
    }

    return [badgeLabel, badgeText];
  }

  renderAcceptAwardButton = (award, acceptedLabel) => {
    if (!employeeService.isAwardAccepted(award)) {
      const buttonId = `button_${award.id}`
      return (
        <Button
          id={buttonId}
          dataTestId={'btn-accept-award'}
          aria-label={`${acceptedLabel} Button`}
          onClick={() => this.props.onAwardNotAcceptedLabelClick(buttonId)}
        >
          {acceptedLabel}
        </Button>
      );
    }
  }

  renderAcceptedAwardLabel = (award, acceptedLabel) => {
    if (employeeService.isAwardAccepted(award)) {
      return <span>({acceptedLabel})</span>;
    }
  }

  renderBadgePopover(label, text) {
    return (
      <Popover
        maxWidth={200}
        position="top-center"
        triggerText={label}
        triggerButtonProps={{
          iconAltText: "Award Row Badge Popover",
          icon: <IconQuestionCircle aria-hidden={true}/>
        }}>
        {() => (
          <Text>{text}</Text>
        )}
      </Popover>
    );
  }

  getAcceptAwardLabel(award, t) {
    return !award.acceptance_status || award.acceptance_status.code === "U" ?
      t('StockAwards.Award.AcceptAwardLabel')
      : '';
  }

  renderRowTitle(award, isRSU, title, t) {
    const awardShares = formatNumber(this.props.locale, award.shares_awarded);
    const subtitle = t('StockAwards.Award.AwardInfo', {
      share_quantity: awardShares,
      award_type: isRSU ? 'RSUs' : 'Options',
      share_date: Moment(award.awarded_date).format('ll')
    });

    const acceptedLabel = employeeService.isAwardAccepted(award) ?
      t('StockAwards.Award.AcceptedLabel', {
        accepted_date: Moment(award.acceptance_date).format('ll')
      })
      : this.getAcceptAwardLabel(award, t);

    const taxBadgeLabel = this.getTaxBadge(award, this.props.locale);
    let [grantBadgeLabel, grantBadgeText] = this.getAwardGrantBadge(t, award.grant_type, award.award_region);

    return (
      <Row gridGap="S100" flexWrap="wrap">
        <Col>
          <Row columnGap="S200" alignItems="center">
            <TriggerButton
              dataTestId={'expand-btn'}
              aria-label={title}
              aria-controls={title}
              iconAltText="Award View Vest Section Trigger Button"
              isExpanded={this.state.isExpanded}
              onClick={() => this.onRowExpandButtonClick()}>
            </TriggerButton>

            <H3 fontWeight={ROW_HEADER_TITLE_FONT_WEIGHT} fontSize={ROW_HEADER_TITLE_FONT_SIZE}>
              {title}
            </H3>

            {taxBadgeLabel &&
              this.renderBadgePopover(taxBadgeLabel, t('StockAwards.Award.TooltipText'))
            }

            {grantBadgeLabel && grantBadgeText &&
              this.renderBadgePopover(grantBadgeLabel, grantBadgeText)
            }
          </Row>

          <Row padding={{left: ROW_HEADER_LEFT_MARGIN}}>
            <Text fontSize={ROW_HEADER_INFO_FONT_SIZE}>
              {subtitle} {acceptedLabel && this.renderAcceptedAwardLabel(award, acceptedLabel)}
            </Text>
          </Row>
        </Col>
        <Col padding={{left: 30}}>
          {acceptedLabel && this.renderAcceptAwardButton(award, acceptedLabel)}
        </Col>
      </Row>
    )
  }

  renderNextVestInfo(award, isRSU, isOption, t) {
    const nextVest = employeeService.getNextVest(award);
    const electionText = this.getElectionText(t, nextVest);
    const nextVestShares = nextVest ? formatNumber(this.props.locale, nextVest.vest_shares) : 0;

    let transactions_shares_transacted = 0;
    if (award.transactions !== undefined) {
      for (let i = 0; i < award.transactions.length; i++) {
        let transaction = award.transactions[i];
        transactions_shares_transacted += transaction.shares_transacted;
      }
    }

    return (
      <>
        {!award.fully_vested && isRSU &&
          <Row gridGap="S400">
            <View>
              <Text fontSize="T100" color="neutral70">
                {t('StockAwards.Award.NextVestLabel')}
              </Text>
            </View>
            <Col>
              <View>
                <Text fontWeight="bold" fontSize="T100">
                  {nextVest ? Moment(nextVest.vest_date).format('ll') : ""}
                </Text>
              </View>
              <View>
                <Text fontSize="T100">
                  {t('StockAwards.Award.NextVestInfo', {
                    share_quantity: nextVestShares,
                    election: electionText
                  })}
                </Text>
              </View>
            </Col>
          </Row>
        }

        {!award.fully_vested && isOption &&
          <View>
            {transactions_shares_transacted < award.shares_awarded &&
              <Link href={this.props.brokerUrl} target="_blank">
                {t('StockAwards.Award.ExerciseAvailableStocksLink')}
              </Link>
            }
            <Text fontSize="T100">
              {t('StockAwards.Award.StrikePriceInfo', {
                value: formatCurrency(this.props.locale, award.strike_price)
              })}
            </Text>
          </View>
        }

        {award.fully_vested &&
          <Chip>{t('StockAwards.Award.FullyVestedLabel')}</Chip>
        }
      </>
    )
  }

  renderRowInfo(award, isRSU, isOption, title, t) {
    const awardMenuOptions = this.getRowMenuOptions(t, award);

    return (
      <WithBreakpoints>
        {({matches}) => (
          <>
            {matches.s &&
              <Col gridGap="S300" flexWrap="wrap">
                <Row>
                  {this.renderRowTitle(award, isRSU, title, t)}
                  <RowActionMenu
                    title={award.amazon_award_name}
                    menuItems={awardMenuOptions}
                    onMenuItemClick={this.onRowMenuItemClick}
                  />
                </Row>
                <Row margin={{left: ROW_HEADER_LEFT_MARGIN}}>
                  {this.renderNextVestInfo(award, isRSU, isOption, t)}
                </Row>
              </Col>
            }

            {!matches.s &&
              <Row dataTestId={'award-row-non-s'} justifyContent="space-between" gridGap="S200">
                <Col width={700}>
                  {this.renderRowTitle(award, isRSU, title, t)}
                </Col>
                <Col width={400}>
                  {this.renderNextVestInfo(award, isRSU, isOption, t)}
                </Col>
                <Col>
                  <RowActionMenu
                    title={award.amazon_award_name}
                    menuItems={awardMenuOptions}
                    onMenuItemClick={this.onRowMenuItemClick}
                  />
                </Col>
              </Row>
            }
          </>
        )}
      </WithBreakpoints>
    );
  }

  render() {
    const {t, award} = this.props;
    const isRSU = employeeService.isRSU(award);
    const isOption = employeeService.isOption(award);

    const transactions = employeeService.getTransactionsFromAward(award);
    const remainingVests = employeeService.getVestsFromAward(award, false);
    const vestedOptions = employeeService.getVestsOptionsFromAward(award);

    const documents = award.documents;

    const title = `${award.amazon_award_id} | ${award.amazon_award_name}`;

    return (
      <>
        {this.renderRowInfo(award, isRSU, isOption, title, t)}

        <ExpanderContent dataTestId={'expanded-content'} id={title} isExpanded={this.state.isExpanded}>
          <Col backgroundColor="neutral05" padding="S400">
            {transactions && transactions.length > 0 &&
              <VestsArea
                title={isRSU ? t('StockAwards.VestsSection.CompletedVestsSectionTitle') : t('StockAwards.VestsSection.ExercisesVestsSectionTitle')}
                transactions={transactions}
                isRSU={isRSU}
                footerMid={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)[0]}
                footerBottom={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)[1]}
                broker={employeeService.getBrokersDisplay(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)}
                onVestClick={this.onTransactionClick}
              />
            }

            {isOption && vestedOptions && vestedOptions.length > 0 &&
              <VestsArea
                title={t('StockAwards.VestsSection.CompletedVestsSectionTitle')}
                vests={vestedOptions}
                isRSU={isRSU}
                footerMid={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)[0]}
                footerBottom={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)[1]}
                broker={employeeService.getBrokersDisplay(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)}
                onVestClick={this.onVestClick}
              />
            }

            {remainingVests && remainingVests.length > 0 &&
              <VestsArea
                title={t('StockAwards.VestsSection.RemainingVestsSectionTitle')}
                vests={remainingVests}
                award={award}
                isRSU={isRSU}
                footerMid={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)[0]}
                footerBottom={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)[1]}
                broker={employeeService.getBrokersDisplay(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)}
                onVestClick={this.onVestClick}
              />
            }

            {documents && documents.length > 0 &&
              <DocsArea
                title={t('StockAwards.DocumentSection.Title')}
                documents={documents}
                onDownloadDocumentClick={this.onDocumentClick}
                selectedDoc={this.state.selectedDoc}
              />
            }
          </Col>

          <VestedAwardDetailsDialog
            award={this.state.award}
            brokers={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)}
            transaction={this.state.transaction}
            visible={this.state.isVestedAwardDialogVisible}
            onClose={() => this.setState({isVestedAwardDialogVisible: false})}
          />

          <UnvestedAwardDetailsDialog
            award={award}
            broker={award.tax_broker}
            brokers={employeeService.getBrokers(t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)}
            vest={this.state.vest}
            visible={this.state.isUnvestedAwardDialogVisible}
            onClose={() => this.setState({isUnvestedAwardDialogVisible: false})}
            onChangePaymentMethodClick={this.props.onChangePaymentMethodClick}
          />

        </ExpanderContent>
      </>
    );
  }
}

const mapState = state => ({
  employee: state.employee,
  locale: state.employee.language,
  brokerUrl: employeeService.getEmployeeBrokerUrl(state.employee),
  defaultTaxRate: state.configuration.default_tax_rate,
  fractionalMode: state.static_content.fractional_mode
})

const mapDispatchToProps = dispatch => ({
  actions: {
    employee: bindActionCreators(employeeActions, dispatch)
  }
})

export default connect(mapState, mapDispatchToProps)(withTranslation('translationsV2')(AwardRow))
